import { useState, useEffect } from 'react';
import { Save, Building2, CreditCard, Wallet, QrCode, Check, Zap, Bell, Mail } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../lib/api';
import { toast } from 'sonner';

const cardBrands = [
  { id: 'visa', label: 'Visa' },
  { id: 'mastercard', label: 'Mastercard' },
  { id: 'elo', label: 'Elo' },
  { id: 'hipercard', label: 'Hipercard' },
  { id: 'amex', label: 'American Express' },
  { id: 'diners', label: 'Diners Club' },
];

const pixKeyTypes = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave Aleatória' },
];

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [companyData, setCompanyData] = useState({
    company_name: '',
    app_name: '',
    whatsapp: '',
    email: '',
    phone: '',
    address: '',
    footer_text: '',
    instagram: '',
    facebook: '',
  });

  const [paymentData, setPaymentData] = useState({
    pix_enabled: true,
    pix_key: '',
    pix_key_type: 'cpf',
    pix_receiver_name: '',
    pix_bank: '',
    credit_enabled: true,
    credit_brands: ['visa', 'mastercard', 'elo'],
    credit_installments_max: 12,
    credit_installments_min_value: 50,
    debit_enabled: true,
    debit_brands: ['visa', 'mastercard', 'elo'],
    // Integrações (novos)
    mp_access_token: '',
    mp_webhook_secret: '',
    mp_enabled: false,
    resend_api_key: '',
    notification_email: '',
    notifications_enabled: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      if (response.data.company) {
        setCompanyData(response.data.company);
      }
      if (response.data.payment) {
        setPaymentData(response.data.payment);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const notifySettingsUpdated = () => {
    // Notifica o SettingsContext na mesma aba
    window.dispatchEvent(new Event('settings:updated'));
    // Notifica outras abas
    try {
      localStorage.setItem('kitstop:settings:updated', String(Date.now()));
    } catch (e) {
      // ignore storage errors
    }
  };

  const saveCompanySettings = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings/company', companyData);
      notifySettingsUpdated();
      toast.success('Dados da empresa salvos! O site foi atualizado.');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const savePaymentSettings = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings/payment', paymentData);
      notifySettingsUpdated();
      toast.success('Configurações de pagamento salvas!');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const toggleCreditBrand = (brandId) => {
    setPaymentData(prev => ({
      ...prev,
      credit_brands: prev.credit_brands.includes(brandId)
        ? prev.credit_brands.filter(b => b !== brandId)
        : [...prev.credit_brands, brandId]
    }));
  };

  const toggleDebitBrand = (brandId) => {
    setPaymentData(prev => ({
      ...prev,
      debit_brands: prev.debit_brands.includes(brandId)
        ? prev.debit_brands.filter(b => b !== brandId)
        : [...prev.debit_brands, brandId]
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="Configurações">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Configurações">
      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="company" className="gap-2">
            <Building2 className="w-4 h-4" />
            Empresa
          </TabsTrigger>
          <TabsTrigger value="payment" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Pagamentos
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Zap className="w-4 h-4" />
            Integrações
          </TabsTrigger>
        </TabsList>

        {/* Company Settings Tab */}
        <TabsContent value="company">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>
                Configure as informações que aparecerão no app e no rodapé
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nome da Empresa</Label>
                  <Input
                    id="company_name"
                    value={companyData.company_name}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Razão social ou nome fantasia"
                    data-testid="settings-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app_name">Nome do App</Label>
                  <Input
                    id="app_name"
                    value={companyData.app_name}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, app_name: e.target.value }))}
                    placeholder="Nome exibido no app"
                    data-testid="settings-app-name"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp (com DDD)</Label>
                  <Input
                    id="whatsapp"
                    value={companyData.whatsapp}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, whatsapp: e.target.value }))}
                    placeholder="5511999999999"
                    data-testid="settings-whatsapp"
                  />
                  <p className="text-xs text-muted-foreground">Formato: 5511999999999 (sem espaços)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail de Contato</Label>
                  <Input
                    id="email"
                    type="email"
                    value={companyData.email}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contato@empresa.com.br"
                    data-testid="settings-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone Fixo (opcional)</Label>
                  <Input
                    id="phone"
                    value={companyData.phone || ''}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(11) 3333-3333"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Endereço (opcional)</Label>
                  <Input
                    id="address"
                    value={companyData.address || ''}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Rua, número, cidade"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="footer_text">Texto Institucional (Rodapé)</Label>
                <Textarea
                  id="footer_text"
                  value={companyData.footer_text}
                  onChange={(e) => setCompanyData(prev => ({ ...prev, footer_text: e.target.value }))}
                  placeholder="Texto que aparece no rodapé do site"
                  rows={3}
                  data-testid="settings-footer-text"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="instagram">Instagram (opcional)</Label>
                  <Input
                    id="instagram"
                    value={companyData.instagram || ''}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, instagram: e.target.value }))}
                    placeholder="@seuinstagram"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="facebook">Facebook (opcional)</Label>
                  <Input
                    id="facebook"
                    value={companyData.facebook || ''}
                    onChange={(e) => setCompanyData(prev => ({ ...prev, facebook: e.target.value }))}
                    placeholder="URL do Facebook"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveCompanySettings} disabled={saving} data-testid="save-company-settings">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Salvando...' : 'Salvar Dados da Empresa'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings Tab */}
        <TabsContent value="payment" className="space-y-6">
          {/* PIX Settings */}
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100">
                    <QrCode className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">PIX</CardTitle>
                    <CardDescription>Configure sua chave PIX para recebimentos</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentData.pix_enabled}
                  onCheckedChange={(checked) => setPaymentData(prev => ({ ...prev, pix_enabled: checked }))}
                  data-testid="pix-enabled-switch"
                />
              </div>
            </CardHeader>
            {paymentData.pix_enabled && (
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo da Chave</Label>
                    <Select
                      value={paymentData.pix_key_type}
                      onValueChange={(value) => setPaymentData(prev => ({ ...prev, pix_key_type: value }))}
                    >
                      <SelectTrigger data-testid="pix-key-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pixKeyTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pix_key">Chave PIX</Label>
                    <Input
                      id="pix_key"
                      value={paymentData.pix_key}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, pix_key: e.target.value }))}
                      placeholder="Sua chave PIX"
                      data-testid="pix-key-input"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pix_receiver_name">Nome do Recebedor</Label>
                    <Input
                      id="pix_receiver_name"
                      value={paymentData.pix_receiver_name}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, pix_receiver_name: e.target.value }))}
                      placeholder="Nome que aparece no PIX"
                      data-testid="pix-receiver-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pix_bank">Banco</Label>
                    <Input
                      id="pix_bank"
                      value={paymentData.pix_bank}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, pix_bank: e.target.value }))}
                      placeholder="Ex: Nubank, Bradesco, etc."
                      data-testid="pix-bank"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Credit Card Settings */}
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">Cartão de Crédito</CardTitle>
                    <CardDescription>Configure as opções de crédito</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentData.credit_enabled}
                  onCheckedChange={(checked) => setPaymentData(prev => ({ ...prev, credit_enabled: checked }))}
                  data-testid="credit-enabled-switch"
                />
              </div>
            </CardHeader>
            {paymentData.credit_enabled && (
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-3 block">Bandeiras Aceitas</Label>
                  <div className="flex flex-wrap gap-3">
                    {cardBrands.map(brand => (
                      <label
                        key={brand.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          paymentData.credit_brands.includes(brand.id)
                            ? 'bg-primary/10 border-primary'
                            : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={paymentData.credit_brands.includes(brand.id)}
                          onCheckedChange={() => toggleCreditBrand(brand.id)}
                        />
                        <span className="text-sm">{brand.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="credit_installments_max">Máximo de Parcelas</Label>
                    <Select
                      value={paymentData.credit_installments_max.toString()}
                      onValueChange={(value) => setPaymentData(prev => ({ ...prev, credit_installments_max: parseInt(value) }))}
                    >
                      <SelectTrigger data-testid="credit-installments">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="credit_installments_min_value">Valor Mínimo por Parcela (R$)</Label>
                    <Input
                      id="credit_installments_min_value"
                      type="number"
                      value={paymentData.credit_installments_min_value}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, credit_installments_min_value: parseFloat(e.target.value) }))}
                      placeholder="50.00"
                      data-testid="credit-min-value"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Debit Card Settings */}
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Wallet className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">Cartão de Débito</CardTitle>
                    <CardDescription>Configure as opções de débito</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentData.debit_enabled}
                  onCheckedChange={(checked) => setPaymentData(prev => ({ ...prev, debit_enabled: checked }))}
                  data-testid="debit-enabled-switch"
                />
              </div>
            </CardHeader>
            {paymentData.debit_enabled && (
              <CardContent>
                <Label className="mb-3 block">Bandeiras Aceitas</Label>
                <div className="flex flex-wrap gap-3">
                  {cardBrands.slice(0, 4).map(brand => (
                    <label
                      key={brand.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        paymentData.debit_brands.includes(brand.id)
                          ? 'bg-primary/10 border-primary'
                          : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={paymentData.debit_brands.includes(brand.id)}
                        onCheckedChange={() => toggleDebitBrand(brand.id)}
                      />
                      <span className="text-sm">{brand.label}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          <div className="flex justify-end">
            <Button onClick={savePaymentSettings} disabled={saving} data-testid="save-payment-settings">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Configurações de Pagamento'}
            </Button>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          {/* Mercado Pago */}
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-sky-100">
                    <Zap className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">Mercado Pago — PIX Automático</CardTitle>
                    <CardDescription>
                      Confirmação automática de pagamento via webhook
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentData.mp_enabled}
                  onCheckedChange={(checked) => setPaymentData(prev => ({ ...prev, mp_enabled: checked }))}
                  data-testid="mp-enabled-switch"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-900 space-y-1">
                <p className="font-semibold">📋 Como obter o ACCESS_TOKEN:</p>
                <ol className="list-decimal list-inside space-y-0.5 ml-1">
                  <li>Acesse <a href="https://www.mercadopago.com.br/developers/panel" target="_blank" rel="noreferrer" className="underline">Mercado Pago Developers</a> e faça login</li>
                  <li>Crie uma Aplicação → seção <strong>Credenciais de Produção</strong></li>
                  <li>Copie o <strong>Access Token</strong> e cole abaixo</li>
                  <li>Em <strong>Webhooks</strong>, cole esta URL: <code className="bg-white px-1 rounded">https://kitstopdealimentos.com.br/api/payments/mp/webhook</code></li>
                  <li>Salve a <strong>Chave Secreta de Assinatura</strong> e cole no campo abaixo</li>
                </ol>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mp_token">Access Token (Produção)</Label>
                <Input
                  id="mp_token"
                  type="password"
                  value={paymentData.mp_access_token || ''}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, mp_access_token: e.target.value }))}
                  placeholder="APP_USR-xxxxxxxx..."
                  data-testid="mp-token-input"
                />
                <p className="text-xs text-muted-foreground">Será armazenado de forma segura no servidor</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mp_secret">Chave Secreta do Webhook (HMAC)</Label>
                <Input
                  id="mp_secret"
                  type="password"
                  value={paymentData.mp_webhook_secret || ''}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, mp_webhook_secret: e.target.value }))}
                  placeholder="Cole a chave secreta exibida no painel do Mercado Pago"
                  data-testid="mp-secret-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Notificações */}
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <Bell className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="font-heading">Notificações Automáticas</CardTitle>
                    <CardDescription>
                      Receba alertas no painel, e-mail e WhatsApp quando o cliente pagar
                    </CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentData.notifications_enabled !== false}
                  onCheckedChange={(checked) => setPaymentData(prev => ({ ...prev, notifications_enabled: checked }))}
                  data-testid="notifications-enabled-switch"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <p>
                  <strong>📡 Canais ativos:</strong>
                </p>
                <ul className="list-disc list-inside mt-1 ml-1 space-y-0.5">
                  <li><strong>Painel Admin</strong> (sino vermelho no canto superior) — sempre ativo</li>
                  <li><strong>E-mail</strong> — requer <strong>Resend API Key</strong> abaixo</li>
                  <li><strong>WhatsApp</strong> — usa o WhatsApp da empresa (Configurações → Empresa)</li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notif_email">E-mail para Notificações</Label>
                <Input
                  id="notif_email"
                  type="email"
                  value={paymentData.notification_email || ''}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notification_email: e.target.value }))}
                  placeholder="Vazio = usa o e-mail da empresa"
                  data-testid="notif-email-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Resend */}
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rose-100">
                  <Mail className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <CardTitle className="font-heading">Resend — Envio de E-mails</CardTitle>
                  <CardDescription>
                    100 e-mails/dia grátis no plano free
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900">
                <p className="font-semibold">📧 Como obter a API Key:</p>
                <ol className="list-decimal list-inside ml-1 mt-1 space-y-0.5">
                  <li>Crie conta em <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a> (gratuito)</li>
                  <li>Vá em <strong>API Keys</strong> → <strong>Create</strong></li>
                  <li>Copie a chave (começa com <code className="bg-white px-1 rounded">re_</code>) e cole abaixo</li>
                </ol>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resend_key">Resend API Key</Label>
                <Input
                  id="resend_key"
                  type="password"
                  value={paymentData.resend_api_key || ''}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, resend_api_key: e.target.value }))}
                  placeholder="re_xxxxxxxxxxxxxxxxx"
                  data-testid="resend-key-input"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={savePaymentSettings} disabled={saving} data-testid="save-integrations">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Integrações'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminSettings;
